import { LandingCard } from '../components/LandingCard.js';
import { RegistrationPanel } from '../components/RegistrationPanel.js';
import { createLogger } from '../game/utils/Logger.js';

const logger = createLogger('LandingPage');

// Extend Window interface for global slider functions
// Note: electronAPI is already declared globally in GrpcConnection.ts
declare global {
    interface Window {
        currentSlideIndex: number;
        goToSlide: (index: number) => void;
    }
}

/**
 * Detect if running inside Electron vs regular browser.
 * In Electron, preload.ts exposes window.electronAPI via contextBridge.
 * In a regular browser (Vercel), this will be undefined.
 */
function isElectron(): boolean {
    return typeof window.electronAPI !== 'undefined';
}

// GitHub releases page for downloading the Electron client
const DOWNLOAD_URL = 'https://github.com/starlightweave-labs/aetherweave-gambit/releases';

document.addEventListener('DOMContentLoaded', () => {
    const container = document.getElementById('hero-cards') as HTMLElement | null;

    // Bind Play Buttons - behavior differs between Electron and Web
    const playButtons = document.querySelectorAll<HTMLElement>('.btn-play:not(.btn-lite), .cta-button');

    if (isElectron()) {
        // ELECTRON MODE: Show login/register panel
        const authPanel = new RegistrationPanel();
        playButtons.forEach((btn: HTMLElement) => {
            btn.addEventListener('click', (e: Event) => {
                e.preventDefault();
                authPanel.open();
            });
        });
        logger.info('Running in Electron mode - auth panel enabled');
    } else {
        // WEB MODE: Redirect to download page
        playButtons.forEach((btn: HTMLElement) => {
            if (btn.classList.contains('btn-play')) {
                btn.textContent = 'DOWNLOAD';
            } else if (btn.classList.contains('cta-button')) {
                btn.textContent = 'DOWNLOAD GAME';
            }
            btn.addEventListener('click', (e: Event) => {
                e.preventDefault();
                window.open(DOWNLOAD_URL, '_blank');
            });
        });
    }

    if (!container) {
        logger.error('hero-cards container not found');
        return;
    }

    // Create Chessboard Background
    const board = document.createElement('div');
    board.className = 'chessboard';
    container.appendChild(board);

    // Create a deck of floating cards
    // Card Content Templates
    const grandStrategyHTML = `
        <div class="card-content-wrapper card-grand-strategy">
            <h3 class="card-title">GRAND STRATEGY</h3>
            <div class="strategy-list">
                <div class="strategy-item"><span class="icon">⚔️</span> Civilization Conflicts</div>
                <div class="strategy-item"><span class="icon">♟️</span> Individual vs System</div>
                <div class="strategy-item"><span class="icon">⚡</span> Man Against Self</div>
            </div>
            <div class="card-decoration"></div>
        </div>
    `;

    const arcanePhysicsHTML = `
        <div class="card-content-wrapper card-arcane">
            <h3 class="card-title">ARCANE PHYSICS</h3>
            <div class="physics-grid">
                <div class="eq">F = ma</div>
                <div class="eq">d = m/V</div>
                <div class="eq">ΔE = W</div>
                <div class="eq">Q = mcΔT</div>
            </div>
            <div class="physics-footer">
                <div class="atom-icon">⚛️</div>
                <span>Universal Laws</span>
            </div>
        </div>
    `;

    const livingWorldHTML = `
        <div class="card-content-wrapper card-living">
            <h3 class="card-title">LIVING WORLD</h3>
            <div class="living-hierarchy">
                <div class="circle planetary">Planetary</div>
                <div class="connector"></div>
                <div class="circle societal">Societal</div>
                <div class="connector"></div>
                <div class="circle biological">Biological</div>
            </div>
            <p class="living-text">
                Modelled 3 interconnected abstracts.<br>
                <em>Each is alive.</em>
            </p>
        </div>
    `;

    const communityHTML = `
        <div class="card-content-wrapper card-community">
            <h3 class="card-title">COMMUNITY</h3>
            <ul class="community-list">
                <li><span class="icon">💬</span> Discord Forum</li>
                <li><span class="icon">💸</span> Zero-Cost Dev</li>
                <li><span class="icon">🛠️</span> No-Code Expansion</li>
            </ul>
            <div class="community-slogan">
                <span>PLAY</span> • <span>PERSONALIZE</span> • <span>SHARE</span>
            </div>
        </div>
    `;

    const mysticismHTML = `
        <div class="card-content-wrapper card-mysticism">
            <h3 class="card-title">MYSTICISM</h3>
            <div class="mystic-text-block">
                <div class="concept-group">
                    <h4>STORYTELLING</h4>
                    <p>Commentary on the condition of Man.</p>
                    <p>poetry • art • distorted mirror</p>
                </div>
                <div class="divider-small"></div>
                <div class="concept-group">
                    <h4>SYMBOLISM</h4>
                    <p>Culture • Cosmology • Spirituality</p>
                </div>
            </div>
        </div>
    `;

    // Landing page images served from client/sprites/ (relative paths for file:// protocol)
    const cards: LandingCard[] = [
        new LandingCard(-220, -40, 0, 10, -15, -5, grandStrategyHTML, "sprites/landing_covenant_throne.webp", 'image'),
        new LandingCard(-110, 20, 40, -5, 5, 2, arcanePhysicsHTML, "sprites/player_starter_forest_elf.webp", 'image'),
        new LandingCard(0, -60, 80, 15, 0, 0, livingWorldHTML, "sprites/player_starter_andromedan.mp4", 'video'),
        new LandingCard(110, 20, 40, -5, -5, -2, communityHTML, "sprites/landing_consumed_guard.webp", 'image'),
        new LandingCard(220, -40, 0, 10, 15, 5, mysticismHTML, "sprites/boss_aletheia_sage.mp4", 'video')
    ];

    cards.forEach((card: LandingCard) => card.mount(container));

    // Optional: Add simple parallax or floating animation loop
    function animate(): void {
        // Animation loop placeholder - cards hover/float via CSS
        // We could add complex physics here later since this is a "Physics based" game.
        requestAnimationFrame(animate);
    }
    animate();
});

// Glitch text effect logic (random character swap)
const glitchText = document.querySelector('.glitch-text') as HTMLElement | null;
if (glitchText !== null && glitchText !== undefined) {
    const originalText = glitchText.getAttribute('data-text') ?? '';
    glitchText.addEventListener('mouseover', () => {
        let iterations = 0;
        const interval = setInterval(() => {
            glitchText.innerText = originalText.split('')
                .map((_letter: string, index: number) => {
                    if (index < iterations) return originalText[index];
                    return String.fromCharCode(65 + Math.floor(Math.random() * 26));
                })
                .join('');

            if (iterations >= originalText.length) clearInterval(interval);
            iterations += 1 / 3;
        }, 30);
    });
}

/* ROADMAP SLIDER LOGIC */
window.currentSlideIndex = 0;

window.goToSlide = function (index: number): void {
    const slides = document.querySelectorAll<HTMLElement>('.slide');
    const dots = document.querySelectorAll<HTMLElement>('.dot');

    if (!slides.length || index < 0 || index >= slides.length) return;

    // Hide current
    const currentSlide = slides[window.currentSlideIndex];
    if (currentSlide !== null && currentSlide !== undefined) currentSlide.classList.remove('active');
    const currentDot = dots[window.currentSlideIndex];
    if (currentDot !== null && currentDot !== undefined) currentDot.classList.remove('active');

    // Update index
    window.currentSlideIndex = index;

    // Show new
    const newSlide = slides[window.currentSlideIndex];
    if (newSlide !== null && newSlide !== undefined) newSlide.classList.add('active');
    const newDot = dots[window.currentSlideIndex];
    if (newDot !== null && newDot !== undefined) newDot.classList.add('active');
};
